import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Loader2, CircleCheck, CircleX } from "lucide-react";
import Seo from "@/components/Seo";

export default function VerifyEmail() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [state, setState] = useState("verifying"); // verifying | success | error
  const [error, setError] = useState("");
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    if (!token) {
      setState("error");
      setError("This verification link is missing its token. Please use the link from your email.");
      return;
    }
    api
      .post("/auth/verify-email", { token })
      .then(() => setState("success"))
      .catch((err) => {
        setState("error");
        setError(formatApiErrorDetail(err.response?.data?.detail) || "We couldn't verify your email.");
      });
  }, [token]);

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <Seo title="Verify Your Email" description="Confirm your Fire Alarm Blueprint Trainer email address." path="/verify-email" noindex />
      <div className="hidden lg:flex flex-col justify-between p-12 bg-[#0A192F] blueprint-grid relative">
        <Link to="/" className="relative z-10">
          <img src="/logo-firealarmbt-light.png" alt="FireAlarm BT — Fire Alarm Blueprint Trainer" className="h-9 w-auto" />
        </Link>
        <div className="relative z-10 max-w-md">
          <p className="font-heading text-3xl font-black leading-tight mb-4">Confirm your email</p>
          <p className="text-slate-400">Verifying your address keeps your account secure and your training progress yours.</p>
        </div>
        <p className="text-xs text-slate-600 font-mono relative z-10">Training use only</p>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-sm text-center" data-testid="verify-email">
          {state === "verifying" && (
            <div data-testid="verify-email-loading">
              <Loader2 className="animate-spin text-primary mx-auto mb-5" size={40} />
              <h1 className="font-heading text-3xl font-black mb-1">Verifying…</h1>
              <p className="text-slate-400 text-sm">Just a moment while we confirm your email.</p>
            </div>
          )}

          {state === "success" && (
            <div data-testid="verify-email-success">
              <span className="grid place-items-center w-16 h-16 rounded-full bg-green-500/15 text-green-400 mx-auto mb-5"><CircleCheck size={34} /></span>
              <h1 className="font-heading text-3xl font-black mb-1">Email verified</h1>
              <p className="text-slate-400 text-sm mb-8">Your email address is confirmed. You're all set — sign in to continue.</p>
              <Button asChild data-testid="verify-email-signin" className="w-full bg-primary hover:bg-primary/90 text-white h-11">
                <Link to="/login">Go to sign in</Link>
              </Button>
            </div>
          )}

          {state === "error" && (
            <div data-testid="verify-email-error">
              <span className="grid place-items-center w-16 h-16 rounded-full bg-primary/15 text-primary mx-auto mb-5"><CircleX size={34} /></span>
              <h1 className="font-heading text-3xl font-black mb-1">Verification failed</h1>
              <p className="text-slate-400 text-sm mb-8">{error}</p>
              <Button asChild data-testid="verify-email-back" variant="secondary" className="w-full bg-secondary border border-white/10 text-white hover:bg-slate-700 h-11">
                <Link to="/login">Back to sign in</Link>
              </Button>
              <p className="text-xs text-slate-500 mt-4">Signed in already? Open your dashboard to resend the verification email.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
